import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { getDefaultAuthenticatedPath, useAuth } from "./context/AuthContext";
import { useEffectiveAppMode } from "./hooks/useEffectiveAppMode";
import AppLoadingShell from "./components/AppLoadingShell";
import ModeFromUrlSync from "./components/ModeFromUrlSync";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import DesktopCallback from "./components/DesktopCallback";
import { DESKTOP_CALLBACK_PATH } from "./utils/desktopCallback";
import "./styles/App.css";

const EmptyChrome = ({ children }) => children;

const AppRoutes = ({ composition }) => {
  const { isAuthenticated, loading } = useAuth();
  const effectiveMode = useEffectiveAppMode();
  const { pathname } = useLocation();

  // Browser landing page for desktop OAuth: needs no session and must not wait
  // for the auth check. Never shown inside the Electron window itself.
  if (
    pathname === DESKTOP_CALLBACK_PATH &&
    !window.starmeshDesktop?.isElectron
  ) {
    return <DesktopCallback />;
  }

  if (loading) {
    return <AppLoadingShell />;
  }

  const showChat = isAuthenticated && Boolean(composition.ChatProduct);
  const showDeveloperShell =
    isAuthenticated &&
    !showChat &&
    effectiveMode === "developer" &&
    Boolean(composition.DataengProduct);
  const showMantis =
    isAuthenticated &&
    !showChat &&
    !showDeveloperShell &&
    Boolean(composition.MantisProduct);
  const authenticatedHomePath = getDefaultAuthenticatedPath(effectiveMode);
  const AuthenticatedRedirect = composition.AuthenticatedRedirect;

  // Keep SignIn out of the authenticated trees. Mounting it on `/` next to
  // Mantis/Dataeng (especially inside a path="*" splat) left the Google
  // callback on `/` looping on the sign-in page, and nested splat Routes
  // shrink-wrapped the settings shell instead of filling the viewport.
  if (!isAuthenticated) {
    return (
      <div className="App">
        <Routes>
          <Route path="/signup" element={<SignUp />} />
          <Route
            path="*"
            element={<SignIn presentation={composition.authPresentation} />}
          />
        </Routes>
      </div>
    );
  }

  if (showChat) {
    return (
      <div className="App">
        <composition.ChatProduct />
      </div>
    );
  }

  if (showDeveloperShell) {
    return (
      <div className="App">
        <composition.DataengProduct
          AddSourceRoute={composition.AddSourceRoute}
          alternateBrand={composition.dataengAlternateBrand}
        />
      </div>
    );
  }

  if (showMantis) {
    return (
      <div className="App">
        <composition.MantisProduct
          authenticatedHomePath={authenticatedHomePath}
        />
      </div>
    );
  }

  return (
    <div className="App">
      {AuthenticatedRedirect ? (
        <AuthenticatedRedirect homePath={authenticatedHomePath} />
      ) : (
        <Navigate to={authenticatedHomePath} replace />
      )}
    </div>
  );
};

/**
 * Render the shared authenticated shell around enabled product composition.
 *
 * @param {{composition: Object}} props Product routes and optional chrome.
 * @returns {React.ReactElement} Complete browser application.
 * @example
 * <App composition={{ DataengProduct, authPresentation: {} }} />
 */
const App = ({ composition }) => {
  const Chrome = composition.Chrome || EmptyChrome;
  return (
    <Router>
      <Chrome>
        <ModeFromUrlSync />
        <AppRoutes composition={composition} />
      </Chrome>
    </Router>
  );
};

export default App;
